import { create } from 'zustand';
import dayjs from 'dayjs';
import { SHIFT_STATUSES, ShiftOld } from '@libs/models/shifts/shift';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { fetchShiftsList } from '@libs/api/shifts';
import { Filter, FiltersCollection } from '../../filters';
import { DEFAULT_PAGE_SIZE } from '@molecules/dataDisplay';

type State = {
  loading: boolean;
  initiated: boolean;
  data: ShiftOld[];
  page: number;
  total: number;
  perPage: number;
  loadShifts: (
    page: number,
    perPage: number,
    filters?: FiltersCollection,
    sorting?: Filter,
    timezone?: string,
  ) => Promise<void>;
  showDetails: boolean;
  currentShift: ShiftOld | null;
  openShiftDetails: (id: string) => void;
  setCurrentShift: (id: string) => void;
  closeShiftDetails: () => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
};

export const useShiftsListStore = create<State>()((set, get) => ({
  loading: false,
  initiated: false,
  data: [],
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  total: 0,

  loadShifts: async (page, perPage, filters = {}, sorting, timezone) => {
    set({ loading: true });
    const applyFilters = { ...filters };
    // add filter for Deleted status, if it's not already there
    if (!applyFilters.statuses) {
      applyFilters.statuses = {
        key: 'statuses',
        value: [
          SHIFT_STATUSES.ASSIGNED,
          SHIFT_STATUSES.COMPLETED,
          ...(applyFilters.hasAssignee === undefined ? [SHIFT_STATUSES.CANCELED] : []),
          // SHIFT_STATUSES.DELETED,
          SHIFT_STATUSES.EXPIRED,
          SHIFT_STATUSES.OPENED,
          SHIFT_STATUSES.IN_PROGRESS,
          SHIFT_STATUSES.PENDING,
          SHIFT_STATUSES.REASSIGNED,
          SHIFT_STATUSES.UNASSIGNED,
          SHIFT_STATUSES.UNFILLED,
        ],
      };
    }
    if (!applyFilters.startDate) {
      applyFilters.startDate = {
        key: 'startDate',
        value: { timezone: timezone ?? '', from: dayjs().startOf('day') },
      };
    }

    try {
      const data = await fetchShiftsList(page, perPage, applyFilters, sorting);
      set({
        loading: false,
        data: data.items,
        page,
        perPage,
        total: data.total,
      });
    } finally {
      set({ loading: false, initiated: true });
    }
  },
  currentShift: null,
  showDetails: false,
  setCurrentShift: (id) => {
    const { data } = get();
    const shift = data.find((s) => s.id === id);
    set({ currentShift: shift });
  },
  openShiftDetails: (id) => {
    const { data } = get();
    const shift = data.find((s) => s.id === id);
    set({ currentShift: shift, showDetails: true });
  },
  closeShiftDetails: () => {
    set({ showDetails: false });
  },
  setPage: (page) => {
    set({ page });
  },
  setPerPage: (perPage) => {
    set({ perPage });
  },
}));

const fallbackTimezone = TIMEZONE_FALLBACK;
